<script setup>
  import {inject, ref, watch} from "vue";

  import ComponentUnsavedChanges from "@components/Alerts/UnsavedChanges.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import {useForm} from "@inertiajs/vue3";
  import {mobilityRestriction} from "@pages/Patients/Components/PatientAttributes/Enums.js";
  import {MedButton, MedCheckbox, MedFormField, MedLabel, MedSpinner} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientAttributesMobilityAttribute",
  });

  const props = defineProps({
    attribute: {
      type: Object,
      required: true,
    },
    patient: {
      type: Object,
      required: true,
    },
    recentlyAdded: {
      type: Boolean,
      default: false,
    },
  });

  const broadcast = inject("$broadcast");

  const refConfirmationDialog = ref(null);

  const activeEdit = ref(props.recentlyAdded);
  const form = useForm({
    type: props.attribute.type,
    payload: {
      bedridden: props.attribute?.payload?.bedridden ?? false,
      rollator: props.attribute?.payload?.rollator ?? false,
      stick: props.attribute?.payload?.stick ?? false,
      wheelchair: props.attribute?.payload?.wheelchair ?? false,
    },
  });

  watch(
    () => props.recentlyAdded,
    (newValue) => {
      activeEdit.value = newValue;
    },
  );

  function cancelEdit() {
    // was recently added AND was NOT saved once
    if (props.recentlyAdded && !form.wasSuccessful) {
      remove();
    } else {
      form.reset();
      activeEdit.value = false;
    }
  }

  function save() {
    form.put(route("attributes.update", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        activeEdit.value = false;
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }

  function confirmRemove() {
    refConfirmationDialog.value.open();
  }

  function remove() {
    form.delete(route("attributes.destroy", {patient: props.patient.id, attribute: props.attribute.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "patient"],
      onSuccess: () => {
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }
</script>

<template>
  <form :data-test="getTestId('mobility-attribute-form')" @submit.prevent="save">
    <ComponentCard class="group relative hover:bg-gray-100" :test-id="getTestId('mobility-attribute-form')">
      <div class="flex items-baseline justify-between">
        <h6 class="text-xl">Eingeschränkte Mobilität</h6>

        <MedSpinner v-if="form.processing" size="xs" :data-test="getTestId('mobility-attribute-spinner')" />

        <ComponentUnsavedChanges v-else-if="form.isDirty || form.recentlySuccessful" :form="form" />
        <span v-else class="text-xs text-gray-500">Aktualisiert {{ attribute.updated_at }}</span>
      </div>

      <template v-if="activeEdit">
        <ul class="mt-4 flex flex-col space-y-2">
          <li v-for="(label, key) in mobilityRestriction" :key="key">
            <MedFormField orientation="horizontal">
              <MedCheckbox
                v-model="form.payload[key]"
                :data-test="getTestId(`mobility-attribute-form-card-${key}-checkbox`)"
              />

              <MedLabel>
                {{ label }}
              </MedLabel>
            </MedFormField>
          </li>
        </ul>
      </template>

      <template v-else>
        <ul class="mt-0.5 flex min-h-4 flex-col">
          <template v-for="(label, key) in mobilityRestriction" :key="key">
            <li v-if="form.payload[key]">{{ label }}</li>
          </template>
        </ul>

        <div class="absolute right-2 bottom-2 flex gap-2">
          <MedButton
            aria-label="Angaben zur Mobilität bearbeiten"
            flat
            variant="primary"
            icon="edit"
            rounded="full"
            :data-test="getTestId('mobility-attribute-form-card-edit-button')"
            @click="activeEdit = true"
          />

          <MedButton
            aria-label="Angaben zur Mobilität löschen"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('mobility-attribute-form-card-delete-button')"
            @click="confirmRemove()"
          />
        </div>
      </template>

      <template v-if="activeEdit" #actions>
        <MedButton
          type="submit"
          variant="primary"
          :disabled="form.processing"
          :data-test="getTestId('mobility-attribute-form-card-save-button')"
        >
          Speichern
        </MedButton>

        <MedButton
          :disabled="form.processing"
          :data-test="getTestId('mobility-attribute-form-card-edit-button')"
          @click="cancelEdit()"
        >
          Abbrechen
        </MedButton>
      </template>
    </ComponentCard>
  </form>

  <ComponentConfirmationDialog
    ref="refConfirmationDialog"
    title="Sind Sie sicher?"
    content="Die Angabe „Mobilität“ wird gelöscht."
    @confirmed="remove()"
  />
</template>
